import classNames from 'classnames';

interface Props {
  label?: string;
  // Escape hatch for layout concerns (spacing, width). For color, add a
  // colorVariant rather than overriding it here, so the token stays authoritative.
  className?: string;
  type?: 'submit' | 'button';
  children?: React.ReactNode;
  cyTag?: string;
  disabled?: boolean;
  // Shape. `bar` is a full-width action bar.
  variant?: 'rounded' | 'square' | 'bar';
  size?: keyof typeof SIZES;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  colorVariant?: keyof typeof COLORS;
  // Associates this button with a <form id=...> elsewhere in the DOM so a
  // submit button rendered outside the form still submits it. Enables the
  // "action bar below the form" layout used by provider registration.
  form?: string;
  // For labels that may wrap; keeps the size's height as a minimum instead of
  // a fixed height.
  wrap?: boolean;
}

// Every variant carries a complete hover/active pair so pressed states don't
// fall back to the resting color.
//
// Each variant also owns its own `disabled:` treatment. This used to be a single
// unconditional `disabled:bg-primary-t-600` on the shared class list, which is
// fine for filled buttons but fills `outline` solid the moment it is disabled.
//
// Measured, not assumed: `disabled:bg-transparent` on the variant *does*
// currently beat the shared `disabled:bg-primary-t-600` (checked by putting the
// shared utility back and re-running Button.browser.spec -- it stayed green).
// So this move is not load-bearing today. It is still the right shape, because
// winning depends on how Tailwind happens to sort two competing `bg-*`
// utilities, this app has no tailwind-merge to collapse them, and nothing in
// the source tells a future reader which one wins. Per-variant makes each
// disabled state explicit instead of resting on that ordering.
const COLORS = {
  primary:
    'bg-primary-50 hover:bg-primary-100 active:bg-primary-200 text-white disabled:bg-primary-t-600',
  secondary:
    'text-primary-50 bg-primary-t-900 hover:bg-primary-t-800 active:bg-primary-t-700 disabled:bg-primary-t-600',
  tertiary:
    'text-grey-200 bg-grey-900 hover:bg-grey-800 active:bg-grey-700 disabled:bg-primary-t-600',
  // Border-only secondary action. Composed from existing tokens on purpose --
  // adding a new palette entry for this is what the raw-palette lint guardrail
  // exists to prevent. Disabled state dims rather than fills, so the button
  // stays recognisably an outline.
  //
  // Interaction states are scoped with `enabled:`. Measured: a disabled outline
  // button hovered with a real pointer stays transparent today, so this is not
  // a live bug -- but it holds only because of where Tailwind happens to emit
  // `disabled:bg-transparent` relative to `hover:bg-*`. Scoping the hover makes
  // it true by construction instead of by emit order.
  //
  // Border tokens are picked for contrast, not to match the neighbouring
  // outline styles. This border IS the control boundary, so WCAG 1.4.11 wants
  // 3:1 against the surface behind it. Measured against both surfaces this
  // renders on: primary-t-700 is 1.58:1 on white and primary-t-600 1.88:1 --
  // both far under. primary-t-200 is 4.05:1 on white and 3.25:1 on
  // background-1; primary-t-100 (hover) is 4.95:1 and 3.97:1. primary-t-300
  // was rejected: 3.31:1 on white but only 2.66:1 on background-1.
  outline:
    'border border-primary-t-200 text-primary-50 bg-transparent enabled:hover:bg-primary-t-900 enabled:hover:border-primary-t-100 enabled:active:bg-primary-t-800 disabled:bg-transparent disabled:opacity-50',
  // NOTE: now byte-identical to `secondary`. It previously used text-blue-400 on
  // bg-blue-50 (2.3:1) — illegible, and worse once disabled. Raising the label to
  // primary-50 fixes that but leaves the two variants indistinguishable; they
  // should probably be merged, which means touching the 6 registration-v2 call
  // sites. Left as a follow-up rather than folded into a tokenization pass.
  translucent:
    'text-primary-50 bg-primary-t-900 hover:bg-primary-t-800 active:bg-primary-t-700 disabled:bg-primary-t-600',
  error: 'bg-danger hover:bg-error-100 active:bg-error-200 text-white disabled:bg-primary-t-600',
};

const SIZES = {
  sm: 'h-9 px-3',
  md: 'h-[50px] p-4',
  lg: 'h-14 px-6',
};

// Parallel to SIZES, for `wrap`: a minimum height instead of a fixed one, so
// a label that wraps to two lines can grow the button instead of clipping.
const WRAP_SIZES = {
  sm: 'min-h-9 px-3',
  md: 'min-h-[50px] p-4',
  lg: 'min-h-14 px-6',
};

const MIN_WIDTHS = {
  sm: 'min-w-[88px]',
  md: 'min-w-[126px]',
  lg: 'min-w-[160px]',
};

export const Button = ({
  label,
  children,
  className,
  cyTag,
  disabled = false,
  type = 'submit',
  variant = 'square',
  size = 'md',
  colorVariant = 'primary',
  onClick,
  form,
  wrap = false,
}: Props) => {
  return (
    <button
      disabled={disabled}
      data-cy={cyTag}
      type={type}
      form={form}
      onClick={(e) => {
        onClick?.(e);
      }}
      className={classNames(
        'body-text-sb',
        wrap ? WRAP_SIZES[size] : SIZES[size],
        // No `disabled:bg-*` here -- see the COLORS comment; it lives per-variant.
        'enabled:cursor-pointer disabled:cursor-not-allowed',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-50',
        variant === 'rounded' ? 'rounded-pill' : 'rounded-control',
        variant === 'bar' ? 'w-full min-w-0 sm:min-w-[350px]' : MIN_WIDTHS[size],
        COLORS[colorVariant],
        wrap && 'max-w-full',
        className
      )}
    >
      {children && children}
      <span>{label}</span>
    </button>
  );
};
